import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHistory, clearHistory } from '../services/api';
import { toast } from 'sonner';
import { Clock, Play, Tv2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export default function HistoryPage() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const data = await getHistory();
        setHistory(data);
      } catch (error) {
        toast.error('Failed to load history');
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const handleClearHistory = async () => {
    try {
      await clearHistory();
      setHistory([]);
      toast.success('History cleared');
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  const handleWatch = (channelId) => {
    navigate(`/watch/${channelId}`);
  };

  const formatWatchTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="history-loading">
        <h1 className="text-3xl font-bold">Watch History</h1>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 rounded-xl skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="history-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Watch History</h1>
          <p className="text-white/60 mt-1">{history.length} items</p>
        </div>
        {history.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-full text-red-400 border-red-400/30 hover:bg-red-500/10">
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[rgb(15,15,16)] border-white/10">
              <AlertDialogHeader>
                <AlertDialogTitle>Clear Watch History?</AlertDialogTitle>
                <AlertDialogDescription className="text-white/60">
                  This will permanently delete your entire watch history. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearHistory} className="rounded-full bg-red-500 hover:bg-red-600">
                  Clear History
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {history.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-[rgb(31,31,34)] flex items-center justify-center mb-6">
            <Clock className="w-10 h-10 text-white/30" />
          </div>
          <h3 className="text-xl font-semibold mb-2">No Watch History</h3>
          <p className="text-white/50 max-w-md mb-6">
            Start watching channels to build your history
          </p>
          <Button onClick={() => navigate('/')} className="rounded-full bg-[rgb(124,58,237)]">
            Browse Channels
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => (
            <div
              key={item.id}
              className="group flex items-center gap-4 p-4 rounded-xl bg-[rgb(15,15,16)] border border-white/5 hover:border-[rgb(124,58,237)]/30 cursor-pointer transition-all"
              onClick={() => handleWatch(item.channel.id)}
              data-testid={`history-item-${item.id}`}
            >
              <div className="w-28 h-16 rounded-lg overflow-hidden flex-shrink-0 relative">
                {item.channel.logo ? (
                  <img
                    src={item.channel.logo}
                    alt={item.channel.name}
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ) : (
                  <div className="w-full h-full bg-[rgb(31,31,34)] flex items-center justify-center">
                    <Tv2 className="w-6 h-6 text-white/30" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Play className="w-6 h-6 text-white" />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className="font-medium truncate">{item.channel.name}</h4>
                <p className="text-sm text-white/40">{item.channel.group}</p>
              </div>
              
              <div className="text-right">
                <p className="text-sm text-white/60">{formatWatchTime(item.watched_at)}</p>
                {item.duration > 0 && (
                  <p className="text-xs text-white/40">{Math.floor(item.duration / 60)}m watched</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
